import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "./config/API";

function FloorRoomsList() {
  const navigate = useNavigate();
  const location = useLocation();

  const floorName = location.state?.floorName ?? "";

  const emailId = JSON.parse(localStorage.getItem("user") || "{}")?.emailId;

  const [rooms, setRooms] = useState([]);

  // =========================================================
  // FILTERS
  // =========================================================

  const [floorFilter, setFloorFilter] = useState("");
  const [roomFilter, setRoomFilter] = useState("");

  // =========================================================
  // DISPLAY DATA AFTER SEARCH
  // =========================================================

  const [filteredRooms, setFilteredRooms] = useState([]);

  // =========================================================
  // DELETE POPUP
  // =========================================================

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // =========================================================
  // DATE FORMATTER
  // =========================================================

  const formatDateOnly = (dateValue) => {
    if (!dateValue) return "-";

    try {
      const date = new Date(dateValue);

      if (Number.isNaN(date.getTime())) {
        const value = String(dateValue);

        if (value.includes("T")) {
          return value.split("T")[0];
        }

        if (value.includes(" ")) {
          return value.split(" ")[0];
        }

        return value;
      }

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return "-";
    }
  };

  // =========================================================
  // FETCH ROOMS
  // =========================================================

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        const payload = {
          emailId,
          searchType: "ALL_ROOMS",
          floorName: floorName || "",
          roomName: "",
          bedName: "",
        };

        const response = await fetch(
          `${API.V1}/floor_rooms/getFloorsRoomsBedsDetails`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.jwtToken}`,
            },
            body: JSON.stringify(payload),
          },
        );

        // =====================================================
        // SESSION EXPIRED
        // =====================================================

        if (response.status === 401) {
          localStorage.clear();
          navigate("/");
          return;
        }

        const data = await response.json();

        if (data?.data?.roomsList) {
          setRooms(data.data.roomsList);
          setFilteredRooms(data.data.roomsList);
        } else {
          setRooms([]);
          setFilteredRooms([]);
        }
      } catch (err) {
        console.error("Fetch Rooms Error:", err);

        setRooms([]);
        setFilteredRooms([]);
      }
    };

    fetchRooms();
  }, [emailId, floorName, navigate]);

  // =========================================================
  // FLOOR OPTIONS
  // =========================================================

  const floorOptions = useMemo(() => {
    return [...new Set(rooms.map((r) => r.floorName).filter(Boolean))];
  }, [rooms]);

  // =========================================================
  // ROOM OPTIONS
  // =========================================================

  const roomOptions = useMemo(() => {
    return [
      ...new Set(
        rooms
          .filter((room) => !floorFilter || room.floorName === floorFilter)
          .map((room) => room.roomName)
          .filter(Boolean),
      ),
    ];
  }, [rooms, floorFilter]);

  // =========================================================
  // SEARCH
  // =========================================================

  const handleSearch = () => {
    let data = [...rooms];

    if (floorFilter) {
      data = data.filter((room) => room.floorName === floorFilter);
    }

    if (roomFilter) {
      data = data.filter((room) => room.roomName === roomFilter);
    }

    setFilteredRooms(data);
  };

  // =========================================================
  // RESET
  // =========================================================

  const handleReset = () => {
    setFloorFilter("");
    setRoomFilter("");
    setFilteredRooms(rooms);
  };

  // =========================================================
  // BACK BUTTON
  // =========================================================

  const handleBack = () => {
    navigate("/dashboard");
  };

  // =========================================================
  // ROOM CLICK
  // =========================================================

  const handleRoomClick = (room) => {
    navigate("/bedsListForm", {
      state: {
        floorName: room.floorName,
        roomName: room.roomName,
        totalBedsBooked: room.totalBedsBooked,
        createdBy: room.createdBy,
      },
    });
  };

  // =========================================================
  // CREATE BEDS
  // =========================================================

  const handleCreateBeds = (room) => {
    // Do not allow inactive room
    if (!room?.roomActiveStatus) {
      return;
    }

    navigate("/floorRoomBedSelector", {
      state: {
        floorName: room.floorName,
      },
    });
  };

  // =========================================================
  // UPDATE
  // =========================================================

  const handleUpdate = (room) => {
    navigate("/updateFloorData", {
      state: {
        floorId: room.floorId,
        roomId: room.roomId,
        totalBedsBooked: room.totalBedsBooked,
        createdBy: room.createdBy,
      },
    });
  };

  // =========================================================
  // OPEN DELETE POPUP
  // =========================================================

  const handleDeleteClick = (room) => {
    setRoomToDelete(room);
    setShowDeletePopup(true);
  };

  // =========================================================
  // CLOSE DELETE POPUP
  // =========================================================

  const handleCloseDeletePopup = () => {
    if (deleteLoading) return;

    setShowDeletePopup(false);
    setRoomToDelete(null);
  };

  // =========================================================
  // DELETE ROOM
  // =========================================================

  const handleDeleteRoom = async (room) => {
    if (!room) return;

    try {
      setDeleteLoading(true);

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        floorId: room.floorId,
        floorName: room.floorName || "",
        roomId: room.roomId,
        roomName: room.roomName || "",
        bedId: 0,
        bedName: "",
        pgAdminEmailId: emailId,
        deleteType: "Room_Delete",
      };

      console.log("Delete Room Payload:", payload);

      const response = await fetch(
        `${API.V1}/floor_rooms/deleteFloorRoomBeds`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.jwtToken}`,
          },
          body: JSON.stringify(payload),
        },
      );

      // =====================================================
      // SESSION EXPIRED
      // =====================================================

      if (response.status === 401) {
        setShowDeletePopup(false);
        setRoomToDelete(null);

        localStorage.clear();
        navigate("/");

        return;
      }

      const data = await response.json();

      console.log("Delete Room Response:", data);

      // =====================================================
      // SUCCESS
      // =====================================================

      if (response.ok && data?.statusCode === 200) {
        const updated = rooms.filter((item) => item.roomId !== room.roomId);

        setRooms(updated);

        setFilteredRooms(
          updated.filter((item) => {
            const floorMatch = !floorFilter || item.floorName === floorFilter;

            const roomMatch = !roomFilter || item.roomName === roomFilter;

            return floorMatch && roomMatch;
          }),
        );

        setShowDeletePopup(false);
        setRoomToDelete(null);
      }

      // =====================================================
      // DELETE FAILED
      // =====================================================
      else {
        setShowDeletePopup(false);
        setRoomToDelete(null);

        alert(data?.message || "Unable to delete room. Please try again.");
      }
    } catch (err) {
      console.error("Delete Room Error:", err);

      setShowDeletePopup(false);
      setRoomToDelete(null);

      alert("500 : Server down, Please try after sometime!");
    } finally {
      setDeleteLoading(false);
    }
  };

  // =========================================================
  // TOTAL BEDS
  // =========================================================

  const totalBedsFromSharing = useMemo(() => {
    return filteredRooms.reduce((sum, room) => {
      const beds = parseInt(room.roomSharingType) || 0;

      return sum + beds;
    }, 0);
  }, [filteredRooms]);

  // =========================================================
  // UI
  // =========================================================

  return (
    <div style={styles.container}>
      {/* ================================================= */}
      {/* BACK BUTTON */}
      {/* ================================================= */}

      <div style={styles.topBar}>
        <button type="button" style={styles.backButton} onClick={handleBack}>
          ← Back
        </button>
      </div>

      {/* ================================================= */}
      {/* TITLE */}
      {/* ================================================= */}

      <h2 style={styles.title}>
        Rooms List {floorName ? `- ${floorName}` : "(All Floors)"}
      </h2>

      {/* ================================================= */}
      {/* COUNT BOXES */}
      {/* ================================================= */}

      <div style={styles.countContainer}>
        <div style={styles.countBox}>Total Rooms : {rooms.length}</div>

        <div style={styles.countBox}>Total Beds : {totalBedsFromSharing}</div>
      </div>

      {/* ================================================= */}
      {/* SEARCH FILTERS */}
      {/* ================================================= */}

      <div style={styles.filterContainer}>
        <select
          style={styles.select}
          value={floorFilter}
          onChange={(e) => {
            setFloorFilter(e.target.value);
            setRoomFilter("");
          }}
        >
          <option value="">All Floors</option>

          {floorOptions.map((floor, index) => (
            <option key={floor} value={floor}>
              {`F${index + 1}`}
            </option>
          ))}
        </select>

        <select
          style={styles.select}
          value={roomFilter}
          onChange={(e) => setRoomFilter(e.target.value)}
        >
          <option value="">All Rooms</option>

          {roomOptions.map((room, index) => (
            <option key={room} value={room}>
              {`R${index + 1}`}
            </option>
          ))}
        </select>

        <button style={styles.searchBtn} onClick={handleSearch}>
          Search
        </button>

        <button style={styles.resetBtn} onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* ================================================= */}
      {/* TABLE */}
      {/* ================================================= */}

      <div style={styles.tableContainer}>
        {/* HEADER */}

        <div style={styles.headerRow}>
          <div>Room Name</div>
          <div>Sharing</div>
          <div>Beds Booked</div>
          <div>Created By</div>
          <div>Created On</div>
          <div>Updated On</div>
          <div>Status</div>
          <div>Actions</div>
        </div>

        {/* DATA */}

        {filteredRooms.length > 0 ? (
          filteredRooms.map((room) => (
            <div key={room.roomId} style={styles.row}>
              {/* Room Name */}

              <div
                style={styles.roomLink}
                onClick={() => handleRoomClick(room)}
                title={room.roomName}
              >
                {room.roomName}
              </div>

              {/* Sharing Type */}

              <div style={styles.cell} title={room.roomSharingType}>
                {room.roomSharingType?.trim() === "No Beds Created" ? (
                  /*
                   * ACTIVE:
                   *   Clickable
                   *
                   * INACTIVE:
                   *   Disabled / Grey
                   */

                  <span
                    style={
                      room.roomActiveStatus
                        ? styles.noBedsLink
                        : styles.noBedsDisabled
                    }
                    onClick={() => {
                      if (room.roomActiveStatus) {
                        handleCreateBeds(room);
                      }
                    }}
                    title={
                      room.roomActiveStatus
                        ? "Click to create beds"
                        : "Room is inactive"
                    }
                  >
                    {room.roomSharingType}
                  </span>
                ) : (
                  room.roomSharingType || "-"
                )}
              </div>

              {/* Total Beds Booked */}

              <div style={styles.centerCell}>{room.totalBedsBooked ?? "-"}</div>

              {/* Created By */}

              <div style={styles.cell} title={room.createdBy}>
                {room.createdBy || "-"}
              </div>

              {/* Created On */}

              <div style={styles.dateCell}>
                {formatDateOnly(room.createdOn)}
              </div>

              {/* Updated On */}

              <div style={styles.dateCell}>
                {formatDateOnly(room.updatedOn)}
              </div>

              {/* Status */}

              <div
                style={
                  room.roomActiveStatus
                    ? styles.activeStatus
                    : styles.inactiveStatus
                }
              >
                {room.roomActiveStatus ? "Active" : "Inactive"}
              </div>

              {/* Actions */}

              <div style={styles.actionBox}>
                <button
                  style={styles.updateBtn}
                  onClick={() => handleUpdate(room)}
                >
                  Update
                </button>

                <button
                  style={styles.deleteBtn}
                  onClick={() => handleDeleteClick(room)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.noData}>No Rooms Found</div>
        )}
      </div>

      {/* ================================================= */}
      {/* DELETE CONFIRMATION POPUP */}
      {/* ================================================= */}

      {showDeletePopup && roomToDelete && (
        <div style={styles.modalOverlay}>
          <div style={styles.deleteModal}>
            {/* Delete Icon */}

            <div style={styles.deleteIconCircle}>
              <span style={styles.deleteIcon}>🗑️</span>
            </div>

            {/* Title */}

            <h2 style={styles.deleteTitle}>Delete Room?</h2>

            {/* Message */}

            <p style={styles.deleteMessage}>
              Are you sure you want to delete this room?
            </p>

            {/* Room Information */}

            <div style={styles.roomInfo}>
              <div style={styles.roomInfoLabel}>Room</div>

              <div style={styles.roomInfoName}>{roomToDelete.roomName}</div>

              <div style={styles.roomInfoFloor}>{roomToDelete.floorName}</div>
            </div>

            {/* Warning */}

            <div style={styles.warningBox}>
              <span style={styles.warningIcon}>⚠️</span>

              <span>This action cannot be undone.</span>
            </div>

            {/* Buttons */}

            <div style={styles.modalActions}>
              <button
                style={styles.cancelButton}
                onClick={handleCloseDeletePopup}
                disabled={deleteLoading}
              >
                Cancel
              </button>

              <button
                style={{
                  ...styles.confirmDeleteButton,
                  ...(deleteLoading ? styles.disabledButton : {}),
                }}
                onClick={() => handleDeleteRoom(roomToDelete)}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <span style={styles.spinner} />
                    Deleting...
                  </>
                ) : (
                  "Delete Room"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FloorRoomsList;

// =========================================================
// STYLES
// =========================================================

const styles = {
  container: {
    width: "100%",
    boxSizing: "border-box",
    padding: "16px",
    background: "#f4f6f8",
    minHeight: "100vh",
    overflowX: "hidden",
  },

  // =======================================================
  // TOP BAR / BACK BUTTON
  // =======================================================

  topBar: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: "10px",
  },

  backButton: {
    background: "#475569",
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "8px 18px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
    transition: "all 0.2s ease",
  },

  // =======================================================
  // TITLE
  // =======================================================

  title: {
    fontSize: "22px",
    fontWeight: "700",
    marginBottom: "16px",
    color: "#1f2937",
  },

  // =======================================================
  // COUNT BOXES
  // =======================================================

  countContainer: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },

  countBox: {
    background: "#1971c2",
    color: "#fff",
    padding: "10px 18px",
    borderRadius: "7px",
    fontWeight: "600",
    minWidth: "150px",
    textAlign: "center",
    boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
    boxSizing: "border-box",
  },

  // =======================================================
  // FILTER
  // =======================================================

  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "16px",
    flexWrap: "wrap",
    background: "#fff",
    padding: "12px",
    borderRadius: "8px",
    boxShadow: "0 2px 7px rgba(0,0,0,0.08)",
    boxSizing: "border-box",
  },

  select: {
    width: "190px",
    padding: "8px 10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
    boxSizing: "border-box",
  },

  searchBtn: {
    padding: "8px 18px",
    border: "none",
    borderRadius: "6px",
    background: "#1971c2",
    color: "#fff",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
  },

  resetBtn: {
    padding: "8px 18px",
    border: "none",
    borderRadius: "6px",
    background: "#6c757d",
    color: "#fff",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
  },

  // =======================================================
  // TABLE
  // =======================================================

  tableContainer: {
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    boxSizing: "border-box",
  },

  headerRow: {
    display: "grid",
    gridTemplateColumns: "1.05fr 0.95fr 0.95fr 1fr 0.95fr 0.95fr 0.75fr 1.45fr",
    gap: "6px",
    background: "#343a40",
    color: "#fff",
    padding: "11px 10px",
    borderRadius: "7px",
    fontWeight: "700",
    fontSize: "13px",
    alignItems: "center",
    boxSizing: "border-box",
    minWidth: "900px",
  },

  row: {
    display: "grid",
    gridTemplateColumns: "1.05fr 0.95fr 0.95fr 1fr 0.95fr 0.95fr 0.75fr 1.45fr",
    gap: "6px",
    background: "#fff",
    padding: "10px",
    marginTop: "7px",
    borderRadius: "7px",
    alignItems: "center",
    minWidth: "900px",
    boxSizing: "border-box",
    boxShadow: "0 2px 7px rgba(0,0,0,0.07)",
    fontSize: "13px",
  },

  cell: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#374151",
  },

  centerCell: {
    textAlign: "center",
    color: "#374151",
    fontWeight: "500",
  },

  dateCell: {
    whiteSpace: "nowrap",
    color: "#4b5563",
    fontSize: "12px",
  },

  roomLink: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#0d6efd",
    cursor: "pointer",
    fontWeight: "700",
    textDecoration: "underline",
  },

  // =======================================================
  // ACTIVE "NO BEDS CREATED"
  // =======================================================

  noBedsLink: {
    color: "#dc3545",
    fontWeight: "700",
    textDecoration: "underline",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  // =======================================================
  // INACTIVE "NO BEDS CREATED"
  // =======================================================

  noBedsDisabled: {
    color: "#9ca3af",
    fontWeight: "700",
    textDecoration: "line-through",
    cursor: "not-allowed",
    whiteSpace: "nowrap",
    opacity: 0.7,
  },

  actionBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px",
    minWidth: 0,
  },

  updateBtn: {
    background: "#0d6efd",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    padding: "6px 9px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "12px",
    whiteSpace: "nowrap",
  },

  deleteBtn: {
    background: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    padding: "6px 9px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "12px",
    whiteSpace: "nowrap",
  },

  activeStatus: {
    color: "#198754",
    fontWeight: "700",
    whiteSpace: "nowrap",
    textAlign: "center",
  },

  inactiveStatus: {
    color: "#dc3545",
    fontWeight: "700",
    whiteSpace: "nowrap",
    textAlign: "center",
  },

  noData: {
    padding: "25px",
    textAlign: "center",
    color: "#666",
    fontSize: "17px",
    background: "#fff",
    borderRadius: "8px",
    marginTop: "8px",
  },

  // =======================================================
  // DELETE POPUP
  // =======================================================

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.65)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "20px",
    backdropFilter: "blur(4px)",
  },

  deleteModal: {
    width: "430px",
    maxWidth: "100%",
    backgroundColor: "#ffffff",
    borderRadius: "18px",
    padding: "32px",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
    animation: "deletePopupAnimation 0.2s ease-out",
    boxSizing: "border-box",
  },

  deleteIconCircle: {
    width: "70px",
    height: "70px",
    margin: "0 auto 18px",
    borderRadius: "50%",
    backgroundColor: "#fee2e2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  deleteIcon: {
    fontSize: "32px",
  },

  deleteTitle: {
    margin: "0 0 10px",
    color: "#111827",
    fontSize: "24px",
    fontWeight: "700",
  },

  deleteMessage: {
    margin: "0 0 20px",
    color: "#6b7280",
    fontSize: "15px",
    lineHeight: "1.5",
  },

  roomInfo: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    padding: "14px",
    marginBottom: "16px",
  },

  roomInfoLabel: {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "4px",
    textTransform: "uppercase",
    fontWeight: "600",
    letterSpacing: "0.5px",
  },

  roomInfoName: {
    fontSize: "18px",
    color: "#111827",
    fontWeight: "700",
  },

  roomInfoFloor: {
    fontSize: "13px",
    color: "#6b7280",
    marginTop: "3px",
  },

  warningBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fed7aa",
    borderRadius: "8px",
    padding: "10px",
    marginBottom: "24px",
    fontSize: "13px",
    fontWeight: "500",
  },

  warningIcon: {
    fontSize: "16px",
  },

  modalActions: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
  },

  cancelButton: {
    minWidth: "120px",
    padding: "11px 20px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    backgroundColor: "#ffffff",
    color: "#374151",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },

  confirmDeleteButton: {
    minWidth: "140px",
    padding: "11px 20px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  disabledButton: {
    opacity: 0.7,
    cursor: "not-allowed",
  },

  spinner: {
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTop: "2px solid #ffffff",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin 0.7s linear infinite",
  },
};
